import { ApplicationRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { TableWidgetDeleteRowModalComponent } from 'dynamic-form/components/form-widgets/table-widget/table-widget-delete-row-modal/table-widget-delete-row-modal.component';
import { Observable } from 'rxjs';
import { DeleteConfirmationModalComponent } from 'shared/components/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import { InfoConfirmationModalComponent } from 'shared/components/modal/info-confirmation-modal/info-confirmation-modal.component';
import { SessionInactivityModalComponent } from 'shared/components/modal/session-inactivity-modal/session-inactivity-modal.component';
import { ModalOptions } from 'shared/interfaces/modal-options.interface';
import { ModalComponent } from '../components/modal/modal.component';
import { SimpleModalComponent } from '../components/modal/simple-modal/simple-modal.component';

@Injectable({
    providedIn: 'root',
})
export class ModalService {
    private static modalRef: ModalComponent;
    private hostViewChildRef!: ViewContainerRef;

    constructor(private applicationRef: ApplicationRef) {}

    initHost(viewChildRef: ViewContainerRef) {
        // Get the root view container ref directly via the initHost method called from the root app component
        this.hostViewChildRef = viewChildRef;
        // Insert the modal component into the root view container
        const componentRef = this.hostViewChildRef.createComponent(ModalComponent);
        // Get the instance of the modal component
        ModalService.modalRef = componentRef.instance;
    }

    /**
     * Opens a modal containing the specified component and supplies whatever is passed in as data to it
     * @param component - The class name of a component to be displayed in the modal. It should be a class that extends BaseModal
     * @param data - Data to be passed to the modal. An object. The type property of the object determines the type of the modal.
     * @param options - Options for the modal. An object. The options are:
     * @returns {onClose: Observable<any>, index: number} - An observable that will emit the result of the modal and the modal index (used to close it programmatically)
     */
    public openModal<T>(
        component: Type<T>,
        data?: any,
        options?: ModalOptions,
    ): {
        onClose: Observable<any>;
        index: number;
        componentInstance: T;
    } {
        if (!ModalService.modalRef) {
            throw new Error('ModalService: Modal component was used before initialization.');
        }
        return ModalService.modalRef.openModal(component, data, options);
    }

    /**
     * Closes the modal with the given index
     * @param index
     * @param result
     */
    public closeModal(index: number, result?: any) {
        ModalService.modalRef.closeModal(index, result);
    }

    /**
     * Closes all currently open modals
     * @param result
     */
    public closeAllModals(result?: any) {
        ModalService.modalRef.closeAllModals(result);
    }

    /**
     * Opens a simple modal with the given texts
     * @param {string} body - Text to be displayed in the body of the modal. Default: 'Are you sure?'
     * @param {string} title - Text to be displayed in the title of the modal. Default: 'Please confirm'
     * @param {string} yesButtonText - Text to be displayed on the yes button. Default: 'Yes'
     * @param {string} noButtonText - Text to be displayed on the no button. Default: 'No'
     * @param {boolean} [defaultYesButton = true] - Determines if the yes button should be the main button
     * @param {'top'|'side'|'center'} [type = 'center'] - Determines where the modal will show up on the page
     * @returns {onClose: Observable<boolean>, index: number} - An observable that will emit the boolean result of the modal
     *  and the modal index (used to close it programmatically)
     */
    public openSimpleModal(
        body: string | string[] = 'generalKeywords.modal.simpleModal.body',
        title: string = 'generalKeywords.modal.simpleModal.title',
        yesButtonText: string = 'generalKeywords.yes',
        noButtonText: string = 'generalKeywords.no',
        defaultYesButton: boolean = true,
        type: 'top' | 'side' | 'center' = 'center',
    ): { onClose: Observable<boolean>; index: number } {
        return ModalService.modalRef.openModal(
            SimpleModalComponent,
            {
                body,
                title,
                yesButtonText,
                noButtonText,
                defaultYesButton,
                type,
            },
            {
                closeOnOverlayClick: true,
                closeOnOverlayClickResult: false,
                closeOnEscape: true,
                closeOnEscapeResult: false,
            },
        ) as { onClose: Observable<boolean>; index: number };
    }

    /**
     * Opens a simple modal with the given texts
     * @param {string} body - Text to be displayed in the body of the modal. Default: 'Delete'
     * @param {string} title - Text to be displayed in the title of the modal. Default: 'Please confirm that you want to delete the following'
     * @param {string} deletedEntityCompanyName - The company name of the deleted entity for example the company name of the user / user group / etc. To be displayed only for Shiji Users
     * @param {string | string[]} deletedEntityName - The name of the deleted entity, for example the user group name / access group name / user email / etc.
     * @param {string} deletedEntityType - The name of the type of the entity itself, this indicates if the deleted entity is a user / user group / etc.
     * @param {boolean} [isWithCompanyConfirmation = false] - This indicates if the modal should dispaly the confirmation input for deletion.
     * @param {string} confirmationInputPlaceholder - The placeholder text of the confirmation input
     * @param {'top'|'side'|'center'} [type = 'center'] - Determines where the modal will show up on the page
     * @param {boolean} [shouldNotTranslatePlaceholder = false] - Determines whether to translate the received placeholder text
     * @param {string} additionalWarningText - Additional warning text to be displayed in the modal
     * @param {string} mainButtonTextOverwrite - Overwrites the default main button text
     * @param {string} mainButtonClassOverwrite - Overwrites the default main button class
     * @param {boolean} usePrimaryButtonColor - Determines whether to use the primary button color or the default danger button color
     * @returns {onClose: Observable<boolean>, index: number} - An observable that will emit the boolean result of the modal
     *  and the modal index (used to close it programmatically)
     */
    public openDeleteConfirmationModal(
        body: string = 'generalKeywords.defaultDeleteBody',
        title: string = 'generalKeywords.delete',
        deletedEntityCompanyName: string = '',
        deletedEntityName: string | string[] = '',
        deletedEntityType: string = '',
        isWithCompanyConfirmation: boolean = false,
        confirmationInputPlaceholder: string = '',
        type: 'top' | 'side' | 'center' = 'center',
        shouldNotTranslatePlaceholder = false,
        additionalWarningText: string = '',
        mainButtonTextOverwrite: string = '',
        usePrimaryButtonColor: boolean = false,
        useOrderedList: boolean = false,
    ): { onClose: Observable<boolean>; index: number } {
        return ModalService.modalRef.openModal(
            DeleteConfirmationModalComponent,
            {
                body,
                title,
                deletedEntityCompanyName,
                deletedEntityName,
                deletedEntityType,
                isWithCompanyConfirmation,
                confirmationInputPlaceholder,
                type,
                shouldNotTranslatePlaceholder,
                additionalWarningText,
                mainButtonTextOverwrite,
                usePrimaryButtonColor,
                useOrderedList,
            },
            {
                closeOnOverlayClick: true,
                closeOnOverlayClickResult: false,
                closeOnEscape: true,
                closeOnEscapeResult: false,
            },
        ) as { onClose: Observable<boolean>; index: number };
    }

    public openSessionInactivityModal(
        countdownTimer: number,
        type: 'top' | 'side' | 'center' = 'center',
    ): { onClose: Observable<boolean>; index: number } {
        return ModalService.modalRef.openModal(
            SessionInactivityModalComponent,
            {
                countdownTimer,
                type,
            },
            {
                closeOnOverlayClick: true,
                closeOnOverlayClickResult: false,
                closeOnEscape: true,
                closeOnEscapeResult: false,
            },
        ) as { onClose: Observable<boolean>; index: number };
    }

    /**
     * Opens a simple modal with the given texts
     * @param columns - The column definition for the deleted row in order to call the specific cell renderer for each widget type
     * @param {string} deletedEntityValue - The name of the deleted entity, for example the user group name / access group name / user email / etc.
     * @param {string} deletedEntityType - The name of the type of the entity itself, this indicates if the deleted entity is a user / user group / etc.
     * @param {boolean} [isWithCompanyConfirmation = false] - This indicates if the modal should dispaly the confirmation input for deletion.
     * @param {string} confirmationInputPlaceholder - The placeholder text of the confirmation input
     * @param {'top'|'side'|'center'} [type = 'center'] - Determines where the modal will show up on the page
     * @param {boolean} [shouldNotTranslatePlaceholder = false] - Determines whether to translate the received placeholder text
     * @returns {onClose: Observable<boolean>, index: number} - An observable that will emit the boolean result of the modal
     *  and the modal index (used to close it programmatically)
     */
    public openTableWidgetDeleteConfirmationModal(
        columns: any[] = [],
        deletedEntityValue: string = '',
        deletedEntityType: string = '',
        isWithCompanyConfirmation: boolean = false,
        confirmationInputPlaceholder: string = '',
        type: 'top' | 'side' | 'center' = 'center',
        shouldNotTranslatePlaceholder = false,
    ): { onClose: Observable<boolean>; index: number } {
        return ModalService.modalRef.openModal(
            TableWidgetDeleteRowModalComponent,
            {
                columns,
                deletedEntityValue,
                deletedEntityType,
                isWithCompanyConfirmation,
                confirmationInputPlaceholder,
                type,
                shouldNotTranslatePlaceholder,
            },
            {
                closeOnOverlayClick: true,
                closeOnOverlayClickResult: false,
                closeOnEscape: true,
                closeOnEscapeResult: false,
            },
        ) as { onClose: Observable<boolean>; index: number };
    }

    /**
     * Opens a simple modal with the given texts
     * @param {string} body - Text to be displayed in the body of the modal. Default: 'Confirm'
     * @param {string} title - Text to be displayed in the title of the modal. Default: 'Please confirm your action'
     * @param {'top'|'side'|'center'} [type = 'center'] - Determines where the modal will show up on the page
     * @returns {onClose: Observable<boolean>, index: number} - An observable that will emit the boolean result of the modal
     *  and the modal index (used to close it programmatically)
     */
    public openInfoConfirmationModal(
        body: string = 'generalKeywords.defaultConfirmBody',
        title: string = 'generalKeywords.defaultConfirmTitle',
        type: 'top' | 'side' | 'center' = 'center',
    ): { onClose: Observable<boolean>; index: number } {
        return ModalService.modalRef.openModal(
            InfoConfirmationModalComponent,
            {
                body,
                title,
                type,
            },
            {
                closeOnOverlayClick: true,
                closeOnOverlayClickResult: false,
                closeOnEscape: true,
                closeOnEscapeResult: false,
            },
        ) as { onClose: Observable<boolean>; index: number };
    }
}
